import type { PillTone } from "@/components/ui/StatusPill";
import type {
  AppMode,
  DailyContentStatus,
  HomeBlockEffectiveStatus,
  HomeBlockStatus,
  NotificationStatus,
} from "@/lib/api";

type StatusMetaValue = { label: string; tone: PillTone };

const UNKNOWN_STATUS_META: StatusMetaValue = {
  label: "Estado desconocido",
  tone: "warning",
};

function withUnknownFallback<T extends object>(entries: T): T {
  return new Proxy(entries, {
    get(target, property, receiver) {
      if (
        typeof property === "string" &&
        !Object.prototype.hasOwnProperty.call(target, property)
      ) {
        return UNKNOWN_STATUS_META;
      }
      return Reflect.get(target, property, receiver);
    },
  });
}


export const APP_MODE_META: Record<AppMode, { label: string; tone: PillTone; description: string }> = withUnknownFallback({
  normal: {
    label: "Normal",
    tone: "success",
    description: "IESAPP funciona con normalidad.",
  },
  read_only: {
    label: "Solo lectura",
    tone: "warning",
    description: "El contenido se puede ver, pero las funciones interactivas quedan deshabilitadas.",
  },
  maintenance: {
    label: "Mantenimiento",
    tone: "warning",
    description: "IESAPP muestra una pantalla de mantenimiento con mensaje.",
  },
  force_update: {
    label: "Actualizacion forzosa",
    tone: "danger",
    description: "Se bloquea el acceso hasta actualizar por debajo de la version minima.",
  },
  disabled: {
    label: "Deshabilitada",
    tone: "danger",
    description: "IESAPP queda intencionadamente fuera de servicio.",
  },
});

export const HOME_BLOCK_STATUS_META: Record<HomeBlockStatus, { label: string; tone: PillTone }> = withUnknownFallback({
  draft: { label: "Borrador", tone: "neutral" },
  published: { label: "Publicado", tone: "success" },
});

export const HOME_BLOCK_EFFECTIVE_STATUS_META: Record<HomeBlockEffectiveStatus, { label: string; tone: PillTone }> = withUnknownFallback({
  draft: { label: "Borrador", tone: "neutral" },
  scheduled: { label: "Programado", tone: "brand" },
  active: { label: "Activo", tone: "success" },
  finished: { label: "Finalizado", tone: "neutral" },
  disabled: { label: "Deshabilitado", tone: "danger" },
});

export function homeBlockPrimaryStatusMeta(status: HomeBlockStatus, enabled: boolean, effectiveStatus?: HomeBlockEffectiveStatus) {
  if (status === "draft") return HOME_BLOCK_EFFECTIVE_STATUS_META.draft;
  if (!enabled) return HOME_BLOCK_EFFECTIVE_STATUS_META.disabled;
  return HOME_BLOCK_EFFECTIVE_STATUS_META[effectiveStatus ?? "active"];
}

export const DAILY_CONTENT_STATUS_META: Record<DailyContentStatus, { label: string; tone: PillTone }> = withUnknownFallback({
  available: { label: "Disponible", tone: "success" },
  draft: { label: "Borrador", tone: "neutral" },
  published: { label: "Publicado", tone: "success" },
  failed: { label: "Fallido", tone: "danger" },
  archived: { label: "Archivado", tone: "neutral" },
  not_found: { label: "No disponible", tone: "neutral" },
});

export const NOTIFICATION_STATUS_META: Record<NotificationStatus, { label: string; tone: PillTone }> = withUnknownFallback({
  draft: { label: "Borrador", tone: "neutral" },
  pending: { label: "Pendiente", tone: "warning" },
  processing: { label: "Procesando", tone: "warning" },
  scheduled: { label: "Programada", tone: "brand" },
  sent: { label: "Enviada", tone: "success" },
  partial: { label: "Parcial", tone: "warning" },
  cancelled: { label: "Cancelada", tone: "neutral" },
  failed: { label: "Fallida", tone: "danger" },
});
